// /tasks: the DSCF build flow (generated from the data), then every task with
// its steps, cure, inputs, station and who is on it today.
import { useMemo } from "react";
import type { ShelfRow, WeekView } from "../lib/types";
import type { CureRow, StationRow } from "../lib/types";
import { BuildFlow, TaskChip } from "../design";
import { useApi } from "../api/live";
import { paths } from "../api/paths";
import { useNow } from "../lib/now";
import { useTasks } from "../lib/useRegister";
import { minutes } from "../lib/flow";
import { Page } from "../shell/Page";
import { ErrorNote, Loading } from "../shell/ui";

export function TasksPage() {
  const now = useNow();
  const { tasks, error, reload } = useTasks();
  const shelves = useApi<ShelfRow[]>(paths.shelves(), ["stock", "shelf"]);
  const week = useApi<WeekView>(paths.week("current"), ["plan"]);
  const stations = useApi<StationRow[]>(paths.stations(), ["station"]);
  const cures = useApi<CureRow[]>(paths.cures(), ["register"]);
  const who = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const c of week.data?.cells ?? []) if (c.day === now.day && c.kind === "task" && c.task && !(m[c.task] ??= []).includes(c.person)) m[c.task].push(c.person);
    return m;
  }, [week.data, now.day]);
  const stationOf = (step: string) => stations.data?.find((s) => s.steps.includes(step));

  return (
    <Page title="Build flow">
      {!tasks.length || !shelves.data ? (error ? <ErrorNote error={error} retry={reload} /> : <Loading />) : (
        <div className="stack">
          <section className="card">
            <div className="card__head"><h2>The DSCF build flow</h2><span className="small muted">Downstairs first, then upstairs. A clock is a cure: a wait with no labour.</span></div>
            <BuildFlow tasks={tasks} shelves={shelves.data} peopleByTask={who} />
          </section>
          <section className="card scroll-x">
            <div className="card__head"><h2>Tasks</h2><span className="small muted">{tasks.length} tasks the planner plans</span></div>
            <table className="table">
              <thead><tr><th>Task</th><th>Steps</th><th>Takes from</th><th>Puts on</th><th>Cure</th><th>Today</th></tr></thead>
              <tbody>{tasks.map((t) => (
                <tr key={t.code}>
                  <td><TaskChip task={t.code} name={t.name} group={t.group} halfOnly={t.halfOnly} /><div className="small muted">{t.group} · {t.sessionQty} a session{t.isAssembly ? " · each unit is a device" : ""}</div>
                    {t.description ? <div className="small" style={{ maxWidth: 360, marginTop: 4 }}>{t.description}</div> : null}</td>
                  <td>{t.steps.map((s) => (
                    <div key={s.code} className="small"><b>{s.code}</b> {s.name}
                      <div className="muted">{[stationOf(s.code)?.name ?? s.station, minutes(s.minutesPerUnit), s.sop].filter(Boolean).join(" · ")}</div></div>
                  ))}</td>
                  <td className="small">{t.draws.map((d) => d.shelf).join(", ") || "—"}{t.consumables ? <div className="muted">{t.consumables}</div> : null}</td>
                  <td className="small">{t.fills.join(", ") || "—"}</td>
                  <td className="small">{t.cure ?? "—"}</td>
                  <td className="small">{who[t.code]?.join(", ") ?? <span className="muted">Nobody</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </section>
          <div className="grid-2">
            <section className="card scroll-x">
              <div className="card__head"><h2>Stations</h2><span className="small muted">Each with the steps it hosts and its tag</span></div>
              {stations.data ? (
                <table className="table"><thead><tr><th>Station</th><th>Floor</th><th>Steps</th><th>Tag</th></tr></thead>
                  <tbody>{stations.data.map((s) => <tr key={s.id}><td><b>{s.name}</b></td><td>{s.floor}</td><td>{s.steps.join(", ")}</td><td>{s.tag ? <code>{s.tag}</code> : "—"}</td></tr>)}</tbody></table>
              ) : stations.error ? <p className="small muted">Stations aren't named yet.</p> : <Loading />}
            </section>
            <section className="card">
              <div className="card__head"><h2>Cures</h2></div>
              {(cures.data ?? []).map((c) => <div key={c.code} className="kv"><span className="kv__k">{c.code}</span><span className="kv__v">{c.duration}</span></div>)}
            </section>
          </div>
        </div>
      )}
    </Page>
  );
}
